<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { McInputDate, McTooltip } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterType, FilterTypeEnum, useApplyFilter, FilterDefinition } from "@/features/filters";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  placeholder = "YYYY-MM-DD",
  hiddenlabel,
  sectionLabel,
  groupId,
  groupLabel,
  clearButton,
  yearcap,
  labelTooltip = false,
  isHotUpdate = true,
  minDateKey,
  maxDateKey
} = props.filter;

const { t } = useI18n();

const zone = props.zonePrefix || "default";
const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const dateConstraintSource = computed(
  () =>
    (props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied) as Record<
      string,
      Record<string, any>
    >
);
const minDateValue = computed(() =>
  minDateKey ? (dateConstraintSource.value?.[zone]?.[minDateKey]?.value as string) || undefined : undefined
);
const maxDateValue = computed(() =>
  maxDateKey ? (dateConstraintSource.value?.[zone]?.[maxDateKey]?.value as string) || undefined : undefined
);

const inputDateValue = ref("");
const hasUserCleared = ref(false);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = source?.[zone]?.[filterKey];
  return {
    ...(storeValue || { label: "", value: "" }),
    filterType: props.filterType
  };
});

watch(
  selectedFilter,
  (val) => {
    if (!hasUserCleared.value) {
      inputDateValue.value = val?.value || "";
    }
    if (!val?.value) {
      hasUserCleared.value = false;
    }
  },
  { immediate: true }
);

watch(inputDateValue, (val) => {
  if (hasUserCleared.value) {
    hasUserCleared.value = false;
    return;
  }

  const trimmed = val?.trim?.() || "";

  if (trimmed && (minDateKey || maxDateKey)) {
    const minVal = minDateValue.value || "";
    const maxVal = maxDateValue.value || "";

    if ((minVal && trimmed < minVal) || (maxVal && trimmed > maxVal)) {
      hasUserCleared.value = true;
      inputDateValue.value = "";
      if (isHotUpdate) {
        removeFilter(filterKey);
      } else {
        updatePanelSelectedFilters({ filterKey, value: "", label: "", filterType: props.filterType });
      }
      return;
    }
  }

  const filterData = getFilterData(trimmed);

  if (props.filterType === FilterTypeEnum.PANEL_FILTERS) {
    updatePanelSelectedFilters(filterData);
  } else {
    updateAppliedFilters(filterData);
  }
});

const getFilterData = (trimmed: string) => {
  if (!trimmed) return null;
  return {
    filterKey,
    label: trimmed,
    value: trimmed,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  };
};

const updatePanelSelectedFilters = (filterData: any) => {
  if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
  if (filterData) {
    panelSelectedFilters[zone][filterKey] = filterData;
  } else {
    delete panelSelectedFilters[zone][filterKey];
  }
};

const updateAppliedFilters = (filterData: any) => {
  if (filterData) {
    applyFilter(filterKey, filterData);
  } else {
    removeFilter(filterKey);
  }
};

const onClear = () => {
  inputDateValue.value = "";
  hasUserCleared.value = true;
  if (isHotUpdate) {
    removeFilter(filterKey);
  } else {
    // Write an empty sentinel so setAppliedFiltersTags removes this key from filtersApplied on Apply.
    // Deleting the key here would make setAppliedFiltersTags skip it entirely, leaving the stale applied value.
    updatePanelSelectedFilters({ filterKey, value: "", label: "", filterType: props.filterType });
  }
};
</script>

<template>
  <mc-tooltip fit="small">
    <mc-input-date
      slot="trigger"
      v-model="inputDateValue"
      fit="small"
      :yearcap="yearcap || 20"
      :label="t(filterLabel)"
      :placeholder="t(placeholder)"
      :hiddenlabel="hiddenlabel"
      :clearbutton="clearButton"
      :name="filterKey"
      v-bind="{ ...(minDateValue ? { min: minDateValue } : {}), ...(maxDateValue ? { max: maxDateValue } : {}) }"
      @clearbuttonclick.prevent="onClear"
    />
    <span v-if="labelTooltip && inputDateValue">{{ t(filterLabel) }}</span>
  </mc-tooltip>
</template>
